import { Component, Pipe, PipeTransform, ViewEncapsulation, effect, inject, input, signal } from '@angular/core';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { FtGoalSelectorComponent } from '../contest-sheets/ft-goal-selector.component';
import { FtIncentiveSelectorComponent } from '../contest-sheets/ft-incentive-selector.component';

/**
 * Native port of the AngularJS `editGoalsTitle` filter
 * (account-v1/src/components/goals/filters/editGoalsTitle.filter.js). Resolves the
 * campaign goals modal header: `'{GoalsFor} {layer→type label}'`. The labels are
 * translated once; `attendees` is capitalised (v1 `capitalize(translate(...))`).
 */
@Pipe({ name: 'editGoalsTitle' })
export class EditGoalsTitlePipe implements PipeTransform {
    private readonly translate = inject(TranslateService);

    transform(type: number, layer: string): string {
        const t = (k: string) => this.translate.instant(k);
        const cap = (s: string) => (s ? s[0].toUpperCase() + s.slice(1) : s);
        const advocates = t('flipto.account.Advocates');
        const friends = t('flipto.account.Friends');
        const attendees = cap(t('flipto.components.goals.Attendees'));
        const warmLeads = t('flipto.account.WarmLeads');
        const visitors = t('flipto.account.Visitors');
        const map: Record<string, Record<number, string>> = {
            'pre-stay': { 1: advocates, 2: warmLeads },
            'post-stay': { 2: warmLeads, 3: advocates, 4: advocates, 6: advocates },
            'group-advocacy': { 1: advocates, 2: friends, 7: attendees },
            'photo-advocacy': { 2: visitors },
        };
        const label = map[layer] && map[layer][type];
        if (label === undefined) return '';
        return t('flipto.components.goals.GoalsFor') + ' ' + label;
    }
}

/**
 * Native port of the AngularJS `ftEditCampaignGoals` directive — the body of the campaign goals
 * editor: a "no goals" panel, one `.modal-panel` per goal of the requested `types` (delete button +
 * goal/incentive selectors), and an "Add goal" footer (disabled when the property has no incentives).
 * Deferred-save: mutates the host's `campaign.goals.item` array in place; the host's pending-changes
 * panel picks it up via `panel.recompute()`. `filteredByTagIncentives`/`filteredGoals` are derived
 * imperatively in the input setters (not getters) so `*ngFor` keeps stable arrays.
 */
@Component({
    selector: 'ft-edit-campaign-goals-ng',
    imports: [TranslateModule, FtGoalSelectorComponent, FtIncentiveSelectorComponent],
    encapsulation: ViewEncapsulation.None,
    templateUrl: './ft-edit-campaign-goals.component.html'
})
export class FtEditCampaignGoalsComponent {
    /** The campaign.goals.item ARRAY (mutated in place — deferred-save via the host panel). */
    readonly goals = input<any>([]);

    /** ALL property incentives `{ item: [] }`; filtered to `incentiveTag` for the selector. */
    readonly incentives = input<any>();

    readonly culture = input<any>();

    /** The goal types this panel edits (e.g. [3,4,6] advocates / [2] warm-leads). */
    readonly types = input<any>();

    /** The incentive category flag this panel's incentives are tagged with. */
    readonly incentiveTag = input<any>();

    /** v1 `filteredByTagIncentives` ($watchCollection('incentives')). */
    protected readonly filteredByTagIncentives = signal<any>({ item: [] });
    /** v1 `filteredGoals` ($watchCollection('goals')) — STABLE array (recomputed imperatively). */
    protected readonly filteredGoals = signal<any[]>([]);

    constructor() {
        effect(() => {
            this.goals();
            this.incentives();
            this.types();
            this.incentiveTag();
            this.recompute();
        });
    }

    /** v1 `hasNoIncentives`. */
    protected hasNoIncentives(): boolean {
        const incentives = this.incentives();
        return !incentives || !Array.isArray(incentives.item) || incentives.item.length === 0;
    }

    /** v1 `addNewGoal` — push a fresh active goal of the first requested type. */
    protected addNewGoal(): void {
        if (this.hasNoIncentives()) return; // v1 ft-disabled="hasNoIncentives()"
        this.goals().push({ isActive: 1, type: this.types()[0] });
        this.recompute();
    }

    deactivate(goal: any): void {
        goal.isActive = 0;
    }
    activate(goal: any): void {
        goal.isActive = 1;
    }

    /** v1 `remove` — splice the goal out of the FULL goals array, then re-derive the view. */
    protected remove(goal: any): void {
        const goals = this.goals();
        const idx = goals.indexOf(goal);
        if (idx !== -1) goals.splice(idx, 1);
        this.recompute();
    }

    /** v1's two `$watchCollection`s collapsed into one imperative derivation. */
    private recompute(): void {
        const incentiveTag = this.incentiveTag();
        const incentives = this.incentives();
        const types = this.types();
        if (incentiveTag && incentives && Array.isArray(incentives.item)) {
            const tagged: any = { item: [] };
            incentives.item.forEach((incentive: any) => {
                if ((incentive.categoryFlags & incentiveTag) === incentiveTag) {
                    tagged.item.push(incentive);
                }
            });
            this.filteredByTagIncentives.set(tagged);
        }
        if (Array.isArray(types)) {
            this.filteredGoals.set((this.goals() || []).filter((goal: any) => goal && types.includes(goal.type)));
        }
    }
}
